// BB-3 (docs/build_bot_platform.md §3.2.3): per-business concurrency caps.
//
// bot_builder.md's `max_concurrent_tasks` maps here to a small in-process
// per-business semaphore. The authoritative enforcement lives in the node
// server (429 + error.code = 'AO_CONCURRENCY_LIMIT', backend.md §7 endpoint
// table); this module is the client-side mirror so the UI can degrade the
// same way while the app is client-only, and so concurrent sends from the
// same tenant don't stampede the model router.

const DEFAULT_LIMIT=3;

const registry=new Map();

export function getConcurrencyLimit() {
  const override=Number(globalThis.localStorage?.getItem('bb_concurrency_limit') || 0);
  return override > 0 ? override : DEFAULT_LIMIT;
}

function queuesFor(businessId) {
  if (!registry.has(businessId)) {
    registry.set(businessId,{active: 0,queue: []});
  }

  return registry.get(businessId);
}

function release(businessId) {
  const entry=queuesFor(businessId);
  const next=entry.queue.shift();

  if (next) {
    // Hand the freed slot directly to the longest-waiting caller.
    next.resolve();
    return;
  }

  entry.active=Math.max(0,entry.active-1);
}

/**
 * Run `task` under the per-business concurrency cap. Concurrent calls beyond
 * the cap WAIT (queued FIFO) rather than fail — the AO_CONCURRENCY_LIMIT
 * error only fires for genuinely saturated sends (see assertConcurrency).
 * Mirrors the server contract: on rejection the error carries
 * code = 'AO_CONCURRENCY_LIMIT' and the UI surfaces it as a retryable state.
 */
export async function withBusinessConcurrency(businessId,task) {
  const entry=queuesFor(businessId);

  if (entry.active >= getConcurrencyLimit()) {
    await new Promise((resolve)=> {
      entry.queue.push({resolve});
    });
  } else {
    entry.active++;
  }

  try {
    return await task();
  } finally {
    release(businessId);
  }
}

/**
 * Immediate (non-waiting) check used to surface the 429-equivalent error
 * before burning any retry budget. Kept separate from withBusinessConcurrency
 * so flows can choose: interactive chat prefers queueing; flow runs prefer
 * failing fast with AO_CONCURRENCY_LIMIT.
 */
export function assertConcurrency(businessId) {
  const entry=queuesFor(businessId);

  if (entry.active >= getConcurrencyLimit()) {
    const error=new Error(
      `Too many concurrent requests for this business (limit ${getConcurrencyLimit()}). Retry shortly.`
    );
    error.code='AO_CONCURRENCY_LIMIT';
    error.retryable=true;
    throw error;
  }

  return {allowed: true,active: entry.active,limit: getConcurrencyLimit()};
}

/** Test/dev helper: reset all semaphores. */
export function resetConcurrency() {
  registry.clear();
}
